import { apiRequest } from '../request'
import type { ApiResponse } from '../request'

export type StorageVisibility = 'PRIVATE' | 'COMPANY' | 'ORGANIZATION' | 'CUSTOM'

export interface StorageFolder {
    id: number
    parentId: number | null
    name: string
    description: string | null
    color: string | null
    icon: string | null
    hasPassword: boolean
    passwordHint: string | null
    locked: boolean
    visibility: StorageVisibility
    /** Pasta restrita a perfis de acesso. */
    restricted: boolean
    roleIds?: number[]
    isSystem: boolean
    sizeBytes: string
    fileCount: number
    childCount?: number
    createdAt: string
    updatedAt: string
    deletedAt: string | null
}

export interface StorageFile {
    id: number
    folderId: number | null
    name: string
    originalName: string
    extension: string
    mimeType: string
    sizeBytes: string
    width: number | null
    height: number | null
    durationMs: number | null
    pageCount: number | null
    description: string | null
    tags: string[]
    hasPassword: boolean
    passwordHint: string | null
    locked: boolean
    visibility: StorageVisibility
    ownerUserId: number
    currentVersion: number
    downloadCount: number
    lastAccessAt: string | null
    retentionUntil: string | null
    thumbnailUrl: string | null
    createdAt: string
    updatedAt: string
    deletedAt: string | null
}

export interface StorageFileVersion {
    id: number
    fileId: number
    version: number
    sizeBytes: string
    mimeType: string | null
    uploadedById: number | null
    createdAt: string
}

export interface StorageShare {
    id: number
    token: string
    fileId: number | null
    folderId: number | null
    hasPassword: boolean
    expiresAt: string | null
    maxDownloads: number | null
    downloadCount: number
    allowDownload: boolean
    watermark: boolean
    createdById: number
    revokedAt: string | null
    createdAt: string
}

export interface StorageUsage {
    usedBytes: string
    /** Limite efetivo (menor entre plano e teto próprio). null = ilimitado. */
    limitBytes: string | null
    /** Teto concedido pelo plano. null quando o plano é ilimitado. */
    planLimitBytes: string | null
    /** Teto que a organização se impôs, se houver. */
    selfLimitBytes: string | null
    unlimited: boolean
    planGb: number | 'unlimited'
    additionalGb: number
    percent: number
    fileCount: number
}

export interface StorageSettings {
    /** Teto próprio da organização, em bytes. null = usa tudo do plano. */
    selfLimitBytes: string | null
    maxFileBytes: number
    trashRetentionDays: number
    unlockTtlSeconds: number
    blockedExtensions: string[]
    allowPublicShares: boolean
    shareDefaultExpiryDays: number
}

export interface StorageUploadIntent {
    fileId: number
    objectKey: string
    strategy: 'single' | 'multipart'
    uploadUrl?: string
    uploadId?: string
    partUrls?: string[]
    partSize?: number
}

export interface StorageAuditLog {
    id: string
    fileId: number | null
    folderId: number | null
    shareId: number | null
    userId: number | null
    userName: string | null
    action: string
    ip: string | null
    createdAt: string
}

export interface PaginatedFiles {
    items: StorageFile[]
    total: number
    page: number
    limit: number
}

export interface ListFilesParams {
    folderId?: number
    search?: string
    extension?: string
    mimeGroup?: 'image' | 'video' | 'audio' | 'document' | 'other'
    tag?: string
    trash?: boolean
    page?: number
    limit?: number
    orderBy?: 'name' | 'createdAt' | 'updatedAt' | 'sizeBytes'
    orderDir?: 'asc' | 'desc'
}

// ===== Pastas =====

export async function listFolders(
    parentId?: number | null,
    trash?: boolean,
): Promise<ApiResponse<StorageFolder[]>> {
    return apiRequest<StorageFolder[]>('storage/folders', {
        params: {
            ...(parentId ? { parentId } : {}),
            ...(trash ? { trash: 'true' } : {}),
        },
    })
}

export async function getFolderTree(): Promise<ApiResponse<StorageFolder[]>> {
    return apiRequest<StorageFolder[]>('storage/folders/tree', {})
}

export async function getFolderBreadcrumb(
    id: number,
): Promise<ApiResponse<StorageFolder[]>> {
    return apiRequest<StorageFolder[]>(`storage/folders/${id}/breadcrumb`, {})
}

export async function getFolderLocks(
    id: number,
): Promise<ApiResponse<StorageFolder[]>> {
    return apiRequest<StorageFolder[]>(`storage/folders/${id}/locks`, {})
}

export async function createFolder(data: {
    name: string
    parentId?: number | null
    description?: string
    color?: string
    visibility?: StorageVisibility
    password?: string
    passwordHint?: string
    /** Preenchido, a pasta nasce restrita aos perfis informados. */
    roleIds?: number[]
}): Promise<ApiResponse<StorageFolder>> {
    return apiRequest<StorageFolder>('storage/folders', { data }, 'post')
}

export async function getFolderAccess(
    id: number,
): Promise<ApiResponse<number[]>> {
    return apiRequest<number[]>(`storage/folders/${id}/access`, {})
}

export async function setFolderAccess(
    id: number,
    roleIds: number[],
): Promise<ApiResponse<StorageFolder>> {
    return apiRequest<StorageFolder>(
        `storage/folders/${id}/access`,
        { data: { roleIds } },
        'put',
    )
}

export async function updateFolder(
    id: number,
    data: {
        name?: string
        description?: string
        color?: string
        visibility?: StorageVisibility
    },
): Promise<ApiResponse<StorageFolder>> {
    return apiRequest<StorageFolder>(`storage/folders/${id}`, { data }, 'patch')
}

export async function moveFolder(
    id: number,
    targetFolderId: number | null,
): Promise<ApiResponse<StorageFolder>> {
    return apiRequest<StorageFolder>(
        `storage/folders/${id}/move`,
        { data: { targetFolderId: targetFolderId ?? undefined } },
        'patch',
    )
}

export async function unlockFolder(
    id: number,
    password: string,
): Promise<ApiResponse<{ success: boolean }>> {
    return apiRequest<{ success: boolean }>(
        `storage/folders/${id}/unlock`,
        { data: { password } },
        'post',
    )
}

export async function setFolderPassword(
    id: number,
    password: string | null,
    passwordHint?: string,
): Promise<ApiResponse<StorageFolder>> {
    return apiRequest<StorageFolder>(
        `storage/folders/${id}/password`,
        { data: { password: password ?? undefined, passwordHint } },
        'patch',
    )
}

export async function deleteFolder(
    id: number,
): Promise<ApiResponse<{ folders: number }>> {
    return apiRequest<{ folders: number }>(`storage/folders/${id}`, {}, 'delete')
}

export async function restoreFolder(
    id: number,
): Promise<ApiResponse<StorageFolder>> {
    return apiRequest<StorageFolder>(`storage/folders/${id}/restore`, {}, 'post')
}

// ===== Arquivos =====

export async function listFiles(
    params: ListFilesParams,
): Promise<ApiResponse<PaginatedFiles>> {
    return apiRequest<PaginatedFiles>('storage/files', { params })
}

export async function getFile(id: number): Promise<ApiResponse<StorageFile>> {
    return apiRequest<StorageFile>(`storage/files/${id}`, {})
}

export async function createUploadIntent(data: {
    name: string
    mimeType: string
    sizeBytes: number
    folderId?: number | null
    description?: string
    tags?: string[]
    visibility?: StorageVisibility
    width?: number
    height?: number
    durationMs?: number
}): Promise<ApiResponse<StorageUploadIntent>> {
    return apiRequest<StorageUploadIntent>(
        'storage/files/upload-intent',
        { data },
        'post',
    )
}

export async function confirmUpload(
    fileId: number,
    data?: {
        checksumSha256?: string
        uploadId?: string
        parts?: { partNumber: number; etag: string }[]
    },
): Promise<ApiResponse<StorageFile>> {
    return apiRequest<StorageFile>(
        `storage/files/${fileId}/confirm`,
        { data: data ?? {} },
        'post',
    )
}

export async function getDownloadUrl(
    id: number,
    inline?: boolean,
): Promise<ApiResponse<{ url: string; name: string; mimeType: string }>> {
    return apiRequest<{ url: string; name: string; mimeType: string }>(
        `storage/files/${id}/download`,
        { params: inline ? { inline: 'true' } : {} },
    )
}

export async function unlockFile(
    id: number,
    password: string,
): Promise<ApiResponse<{ success: boolean }>> {
    return apiRequest<{ success: boolean }>(
        `storage/files/${id}/unlock`,
        { data: { password } },
        'post',
    )
}

export async function setFilePassword(
    id: number,
    password: string | null,
    passwordHint?: string,
): Promise<ApiResponse<StorageFile>> {
    return apiRequest<StorageFile>(
        `storage/files/${id}/password`,
        { data: { password: password ?? undefined, passwordHint } },
        'patch',
    )
}

export async function updateFile(
    id: number,
    data: {
        name?: string
        description?: string
        tags?: string[]
        visibility?: StorageVisibility
        retentionUntil?: string
    },
): Promise<ApiResponse<StorageFile>> {
    return apiRequest<StorageFile>(`storage/files/${id}`, { data }, 'patch')
}

export async function moveFile(
    id: number,
    targetFolderId: number | null,
): Promise<ApiResponse<StorageFile>> {
    return apiRequest<StorageFile>(
        `storage/files/${id}/move`,
        { data: { targetFolderId: targetFolderId ?? undefined } },
        'patch',
    )
}

export async function copyFile(
    id: number,
    targetFolderId: number | null,
): Promise<ApiResponse<StorageFile>> {
    return apiRequest<StorageFile>(
        `storage/files/${id}/copy`,
        { data: { targetFolderId: targetFolderId ?? undefined } },
        'post',
    )
}

export async function deleteFile(
    id: number,
): Promise<ApiResponse<{ success: boolean }>> {
    return apiRequest<{ success: boolean }>(`storage/files/${id}`, {}, 'delete')
}

export async function restoreFile(
    id: number,
): Promise<ApiResponse<StorageFile>> {
    return apiRequest<StorageFile>(`storage/files/${id}/restore`, {}, 'post')
}

export async function purgeFile(
    id: number,
): Promise<ApiResponse<{ success: boolean }>> {
    return apiRequest<{ success: boolean }>(
        `storage/files/${id}/purge`,
        {},
        'delete',
    )
}

// ===== Versões =====

export async function listVersions(
    id: number,
): Promise<ApiResponse<StorageFileVersion[]>> {
    return apiRequest<StorageFileVersion[]>(`storage/files/${id}/versions`, {})
}

/**
 * Reserva o envio de uma nova versão e devolve a URL pré-assinada.
 *
 * O backend guarda a chave do objeto pendente em cache até o `confirm`: uma
 * versão que não chegou ao R2 não pode aparecer no histórico.
 */
export async function createVersionIntent(
    id: number,
    data: { name: string; mimeType: string; sizeBytes: number },
): Promise<ApiResponse<StorageUploadIntent>> {
    return apiRequest<StorageUploadIntent>(
        `storage/files/${id}/versions/intent`,
        { data },
        'post',
    )
}

export async function confirmVersion(
    id: number,
): Promise<ApiResponse<StorageFile>> {
    return apiRequest<StorageFile>(
        `storage/files/${id}/versions/confirm`,
        { data: {} },
        'post',
    )
}

export async function restoreVersion(
    id: number,
    version: number,
): Promise<ApiResponse<StorageFile>> {
    return apiRequest<StorageFile>(
        `storage/files/${id}/versions/${version}/restore`,
        {},
        'post',
    )
}

// ===== Compartilhamentos =====

export async function listShares(): Promise<ApiResponse<StorageShare[]>> {
    return apiRequest<StorageShare[]>('storage/shares', {})
}

export async function createShare(data: {
    fileId?: number
    folderId?: number
    password?: string
    expiresInDays?: number
    maxDownloads?: number
    allowDownload?: boolean
}): Promise<ApiResponse<StorageShare>> {
    return apiRequest<StorageShare>('storage/shares', { data }, 'post')
}

export async function revokeShare(
    id: number,
): Promise<ApiResponse<{ success: boolean }>> {
    return apiRequest<{ success: boolean }>(`storage/shares/${id}`, {}, 'delete')
}

// ===== Uso, configurações e auditoria =====

export async function getUsage(): Promise<ApiResponse<StorageUsage>> {
    return apiRequest<StorageUsage>('storage/usage', {})
}

/** Consumo por origem do arquivo — responde "o que ocupa meu espaço". */
export interface StorageUsageByOrigin {
    resource: string
    count: number
    sizeBytes: string
}

export async function getUsageByOrigin(): Promise<
    ApiResponse<StorageUsageByOrigin[]>
> {
    return apiRequest<StorageUsageByOrigin[]>('storage/usage/by-origin', {})
}

export async function getStats(): Promise<
    ApiResponse<{ group: string; count: number; sizeBytes: string }[]>
> {
    return apiRequest<{ group: string; count: number; sizeBytes: string }[]>(
        'storage/stats',
        {},
    )
}

export async function getSettings(): Promise<ApiResponse<StorageSettings>> {
    return apiRequest<StorageSettings>('storage/settings', {})
}

export async function updateSettings(
    // `selfLimitBytes` entra como número (bytes) e volta como string, porque no
    // banco é BIGINT — daí o tipo diferente entre envio e resposta.
    data: Partial<Omit<StorageSettings, 'selfLimitBytes'>> & {
        selfLimitBytes?: number | null
    },
): Promise<ApiResponse<StorageSettings>> {
    return apiRequest<StorageSettings>('storage/settings', { data }, 'patch')
}

export async function getAuditLogs(params?: {
    fileId?: number
    folderId?: number
    limit?: number
}): Promise<ApiResponse<StorageAuditLog[]>> {
    return apiRequest<StorageAuditLog[]>('storage/audit', { params })
}

// ===== Envio do arquivo direto ao R2 =====

/**
 * Envia o binário para a URL pré-assinada. Não passa pelo backend — é PUT
 * direto no bucket, o que mantém a API fora do caminho de 2 GB de vídeo.
 *
 * Por ser uma URL assinada de outro host, aqui se usa `fetch` puro: o cliente
 * axios do projeto injetaria os headers de autenticação, que invalidariam a
 * assinatura.
 */
export async function uploadToSignedUrl(
    url: string,
    file: File | Blob,
    contentType: string,
    onProgress?: (percent: number) => void,
): Promise<void> {
    await new Promise<void>((resolve, reject) => {
        const xhr = new XMLHttpRequest()
        xhr.open('PUT', url, true)
        xhr.setRequestHeader('Content-Type', contentType)

        xhr.upload.onprogress = event => {
            if (event.lengthComputable && onProgress) {
                onProgress(Math.round((event.loaded / event.total) * 100))
            }
        }
        xhr.onload = () => {
            if (xhr.status >= 200 && xhr.status < 300) resolve()
            else reject(new Error(`Falha no envio (HTTP ${xhr.status})`))
        }
        // O navegador não expõe ao JavaScript a diferença entre queda de rede e
        // bloqueio por CORS — nos dois casos chega um erro sem status. Como o
        // bloqueio por CORS é a causa comum (bucket sem política, ou domínio
        // novo fora de AllowedOrigins), a mensagem cita as duas.
        xhr.onerror = () =>
            reject(
                new Error(
                    'O armazenamento recusou a conexão. Verifique a política de CORS do bucket ou a sua conexão.',
                ),
            )
        xhr.send(file)
    })
}

/** Envia as partes de um upload multipart e devolve as ETags. */
export async function uploadMultipart(
    partUrls: string[],
    file: File,
    partSize: number,
    onProgress?: (percent: number) => void,
): Promise<{ partNumber: number; etag: string }[]> {
    const parts: { partNumber: number; etag: string }[] = []

    for (let index = 0; index < partUrls.length; index++) {
        const start = index * partSize
        const chunk = file.slice(start, Math.min(start + partSize, file.size))

        const response = await fetch(partUrls[index], {
            method: 'PUT',
            body: chunk,
        })
        if (!response.ok) {
            throw new Error(`Falha ao enviar a parte ${index + 1} do arquivo`)
        }

        // A ETag só é legível pelo JavaScript se o bucket a expuser em
        // ExposeHeaders. Sem ela o envio "termina" e só quebra no fechamento,
        // com erro do S3 difícil de associar à causa — melhor falhar aqui,
        // apontando o que configurar.
        const etag = response.headers.get('ETag')?.replace(/"/g, '')
        if (!etag) {
            throw new Error(
                'O armazenamento não expôs a identificação da parte enviada. Inclua "ETag" em ExposeHeaders na política de CORS do bucket.',
            )
        }

        parts.push({ partNumber: index + 1, etag })
        onProgress?.(Math.round(((index + 1) / partUrls.length) * 100))
    }

    return parts
}
